import { AxiosError } from 'axios';
import { useQuery } from 'react-query';

import { api } from '../../utils/api-client';
import { pathToUrl } from '../../utils/router';
import { ApiRoutesEnum } from '../../utils/routes';
import { CandidateAiParsedCvType, CandidateCvProcessingType } from '../candidates/Candidates.types';

export const CANDIDATE_AI_PARSED_CV_QUERY_KEY = 'candidate-ai-parsed-cv';

type UseGetCandidateAiParsedCvProps = {
  candidateId?: string;
  currentCvUuid?: string | null;
  cvProcessing?: CandidateCvProcessingType;
  enabled: boolean;
};

// The key carries the full CV identity so the cache moves on a CV change.
export const buildCandidateAiParsedCvQueryKey = ({
  candidateId,
  currentCvUuid,
  cvProcessing,
}: Omit<UseGetCandidateAiParsedCvProps, 'enabled'>) => [
  CANDIDATE_AI_PARSED_CV_QUERY_KEY,
  candidateId ?? '',
  currentCvUuid ?? null,
  cvProcessing?.cvHash ?? null,
  cvProcessing?.status === 'READY',
];

export const useGetCandidateAiParsedCv = ({
  candidateId,
  currentCvUuid,
  cvProcessing,
  enabled,
}: UseGetCandidateAiParsedCvProps) =>
  useQuery<CandidateAiParsedCvType | null, AxiosError>(
    buildCandidateAiParsedCvQueryKey({ candidateId, currentCvUuid, cvProcessing }),
    async () => {
      const response = await api.get<CandidateAiParsedCvType | ''>(
        pathToUrl(ApiRoutesEnum.getCandidateAiParsedCv, { id: candidateId ?? '' })
      );

      // 204 has an empty body, so `response.data` is '' rather than an object.
      return response.status === 204 || !response.data ? null : response.data;
    },
    {
      enabled: enabled && !!candidateId,
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 60_000,
      keepPreviousData: true,
    }
  );
